<template>
  <article
    class="faq-item flex flex-column w-full"
    :class="{ active: localIsOpen }"
  >
    <button
      type="button"
      class="faq-button"
      :aria-expanded="localIsOpen"
      :aria-controls="`faq-content-${id}`"
      @click="handleFaqClick"
    >
      <div class="faq-header">
        <h4
          :id="`faq-title-${id}`"
          class="faq-title"
          v-text="title"
        />
        <UIIconComponent
          class="btn icon-btn chevron-btn"
          :icon="'chevron-down'"
        />
      </div>
    </button>
    <Transition
      name="fade"
      @before-enter="transitionBeforeEnter"
      @enter="transitionEnter"
      @before-leave="transitionBeforeLeave"
      @leave="transitionLeave"
    >
      <div
        v-if="localIsOpen"
        :id="`faq-content-${id}`"
        class="faq-content"
        role="region"
        :aria-labelledby="`faq-title-${id}`"
      >
        <span
          class="raw"
          v-html="text"
        />
      </div>
    </Transition>
  </article>
</template>

<script setup lang="ts">
  const props = defineProps<{
    id: string | number
    title: string
    text: string
    isOpen?: boolean
  }>()

  const emit = defineEmits(['toggle'])

  const localIsOpen = ref(props.isOpen)

  function handleFaqClick() {
    emit('toggle', props.id)
  }

  let stopIsOpenWatch: (() => void) | null = null

  onMounted(() => {
    stopIsOpenWatch = watch(
      () => props.isOpen,
      (newValue) => {
        localIsOpen.value = newValue
      },
    )
  })

  onUnmounted(() => {
    stopIsOpenWatch?.()
  })
</script>

<style scoped lang="scss">
  .faq-item {
    position: relative;
    padding: var(--padding-lg) 0;
    border-bottom: 1px solid rgba($color-text, 0.1);

    .faq-button {
      width: 100%;
      text-align: left;
      cursor: pointer;
      display: block;

      &:focus {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }

      &:focus:not(:focus-visible) {
        outline: none;
      }

      &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
      }

      &:hover {
        .icon-btn {
          background-color: $color-highlight;
          border-color: $color-highlight;
        }
      }
    }

    .faq-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: margin-bottom var(--ease-fast-in-slow-out) var(--animation-duration);

      h4 {
        margin-right: 2.4rem;
      }
    }

    .faq-content {
      height: auto;
      overflow: hidden;
      transition: var(--ease-fast-in-slow-out) var(--animation-duration);
    }

    &.active {
      .faq-header {
        margin-bottom: var(--padding-md);
      }

      .icon-btn {
        background-color: $color-highlight;
        border-color: $color-highlight;
      }

      .icon {
        transform: rotate(-180deg);
      }
    }
  }
</style>
